// src/components/quotes/QuoteCard.jsx
//
// THE quote card — the same one in the pipeline list and on the board, so the
// two can never drift. Moved out of MspOsApp.jsx verbatim, plus one row of
// fulfilment chips on any won-or-later card.
//
// ⚠ CHIPS: green fill + tick when done, grey outline + empty circle when not.
// NOT amber or red for not-done — an unticked step is work still to do, not a
// fault, and the card already uses amber for `sent` and red for `lost`;
// reusing them would make "hasn't been invoiced yet" look like a problem.
// NEVER colour alone: the glyph carries the state as well as the fill
// (LICENSING_MFA_COLOUR_CODING_BRIEF.md — marker + colour, never colour-alone).
// All four chips render on every won-or-later card, INCLUDING in the Complete
// column: Craig's requirement is "show me all the statuses so I can see what
// is still to be completed", and a card that hides finished steps can't answer
// that at a glance.
import React from "react";
import { formatQuoteNumber } from "./quoteRecord";
import { FULFILMENT_STEPS, STEP_LABELS, isStepDone, showsFulfilment } from "./quoteBoard";

const STATUS_COLOR = { won: "var(--ops)", lost: "var(--danger)", sent: "var(--warn)" };
const STATUS_BG = { won: "var(--ops-d)", lost: "var(--danger-d)", sent: "var(--warn-d)" };

export function FulfilmentChips({ quote, onToggleStep }) {
  if (!showsFulfilment(quote)) return null;
  return (
    <div data-testid="fulfilment-chips" style={{ display: "flex", gap: 4, flexWrap: "wrap", marginTop: 8 }}>
      {FULFILMENT_STEPS.map((step) => {
        const done = isStepDone(quote, step);
        const label = STEP_LABELS[step];
        const when = done ? new Date(quote.fulfilment[step]).toLocaleDateString() : null;
        return (
          <button
            key={step}
            type="button"
            data-testid={`chip-${step}`}
            aria-pressed={done}
            title={done ? `${label} — done ${when}. Click to untick.` : `${label} — not yet. Click to tick.`}
            /* stopPropagation: the card's own click opens the builder */
            onClick={(e) => { e.stopPropagation(); onToggleStep && onToggleStep(step); }}
            style={{
              fontSize: 9, fontWeight: 700, padding: "2px 7px", borderRadius: 10, cursor: "pointer",
              letterSpacing: "0.3px", lineHeight: 1.4, fontFamily: "inherit",
              border: `1px solid ${done ? "var(--ops)" : "var(--text3)"}`,
              background: done ? "var(--ops-d)" : "var(--s2)",
              color: done ? "var(--ops)" : "var(--text3)",
            }}>
            {done ? "✓" : "○"} {label}
          </button>
        );
      })}
    </div>
  );
}

export default function QuoteCard({
  quote: q, computeQuoteTotals, countAllLineItems, sumOptions, ensureMigrated,
  onOpen, onToggleStep, onDuplicate, draggable = false, onDragStart,
}) {
  const totals = computeQuoteTotals(q);
  const statusColor = STATUS_COLOR[q.status] || "var(--text3)";
  const statusBg = STATUS_BG[q.status] || "var(--s2)";
  const lines = countAllLineItems(q);
  const secs = ensureMigrated(q).sections || [];
  return (
    <div
      data-testid={`quote-card-${q.id}`}
      draggable={draggable || undefined}
      onDragStart={draggable ? onDragStart : undefined}
      style={{ background: "white", border: "1px solid var(--border)", borderLeft: `3px solid ${statusColor}`, borderRadius: 8, padding: 14, cursor: draggable ? "grab" : "pointer", boxShadow: "var(--shadow-sm)", transition: "all 0.15s" }}
      onMouseEnter={(e) => { e.currentTarget.style.boxShadow = "var(--shadow-md)"; e.currentTarget.style.transform = "translateY(-1px)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.boxShadow = "var(--shadow-sm)"; e.currentTarget.style.transform = ""; }}
      onClick={() => onOpen && onOpen(q.id)}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 6, marginBottom: 6 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* ⚠ The Q number sits BESIDE the title, never inside it.
              A pipeline quote's title carries the source proposal's P
              number; a standalone quote's title is whatever was typed
              and is never rewritten. The Q is data about the record. */}
          {q.quoteNumber != null && (
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.06em", color: "var(--text3,#999)", fontVariantNumeric: "tabular-nums", marginBottom: 2 }}>{formatQuoteNumber(q.quoteNumber)}</div>
          )}
          <div style={{ fontSize: 13, fontWeight: 700, color: "var(--text)", lineHeight: 1.3 }}>{q.title || "(untitled quote)"}</div>
        </div>
        <span style={{ fontSize: 9, fontWeight: 700, color: statusColor, background: statusBg, padding: "2px 8px", borderRadius: 10, textTransform: "uppercase", letterSpacing: "0.5px", flexShrink: 0 }}>{q.status}</span>
      </div>
      {q.companyName && <div style={{ fontSize: 11, color: "var(--strat)", marginBottom: 6, fontWeight: 600 }}>{q.companyName}</div>}
      <div style={{ fontSize: 10, color: "var(--text3)", marginBottom: 10 }}>
        {lines} line item{lines !== 1 ? "s" : ""}{secs.length > 0 ? <> · {secs.length} section{secs.length !== 1 ? "s" : ""} · {sumOptions(secs)} option{sumOptions(secs) !== 1 ? "s" : ""}</> : null}
        {(q.autotaskTicketNumber || q.autotaskQuoteId) && <span style={{ marginLeft: 8, color: "var(--ops)", fontWeight: 600 }}>· AT {q.autotaskTicketNumber || `#${q.autotaskQuoteId}`}</span>}
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", paddingTop: 8, borderTop: "1px solid var(--border)" }}>
        <span style={{ fontSize: 10, color: "var(--text3)" }}>Total</span>
        <span style={{ fontSize: 16, fontWeight: 700, color: "var(--text)" }}>${totals.total.toFixed(2)}</span>
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 10, color: "var(--text3)", marginTop: 3, gap: 6 }}>
        <span>Margin {totals.marginPct.toFixed(1)}%</span>
        <span style={{ flex: 1 }} />
        <span>{q.updatedAt ? new Date(q.updatedAt).toLocaleDateString() : ""}</span>
        {/* Duplicate lives on the card — it is where you are standing when you
            think "I want another one of these". No confirm: a draft is harmless
            and reversible. Click, mousedown and dragstart are all stopped so it
            neither opens the source nor starts the board's drag. */}
        {onDuplicate && (
          <button type="button" data-testid="quote-duplicate" className="btn"
            title="Duplicate this quote as a new draft"
            draggable={false}
            onMouseDown={(e) => e.stopPropagation()}
            onDragStart={(e) => { e.preventDefault(); e.stopPropagation(); }}
            onClick={(e) => { e.stopPropagation(); onDuplicate(q.id); }}
            style={{ fontSize: 10, padding: "1px 7px", lineHeight: 1.5 }}>⧉ Duplicate</button>
        )}
      </div>
      <FulfilmentChips quote={q} onToggleStep={onToggleStep} />
    </div>
  );
}
